import { ApiError } from '@/api/ApiError'
import {
  type MemberRow,
  normalizeEmail,
  rowFromLookup,
} from '@/features/OrgMembers/lib/memberRows'
import { fetchLookupUserIdentities } from '@/generated/http-clients/v3ApiComponents'
import { useV3ApiContext } from '@/generated/http-clients/v3ApiContext'
import type { OrganizationMemberRole } from '@/generated/http-clients/v3ApiSchemas'
import { useCallback, useRef, useState } from 'react'

export const MAX_ROWS = 10

export type EntryError =
  | { kind: 'duplicate' }
  | { kind: 'limit' }
  | { kind: 'invalid'; email: string }

export type MemberRowPatch = Partial<{
  role: OrganizationMemberRole
  firstName: string
  lastName: string
  selectedUserId: string
}>

type RowsUpdater = (rows: MemberRow[]) => MemberRow[]

function applyPatch(row: MemberRow, patch: MemberRowPatch): MemberRow {
  const next: MemberRow = { ...row }
  if (patch.role !== undefined) {
    next.role = patch.role
  }
  if (next.status === 'new') {
    next.firstName = patch.firstName ?? next.firstName
    next.lastName = patch.lastName ?? next.lastName
  }
  if (next.status === 'multiple') {
    next.selectedUserId = patch.selectedUserId ?? next.selectedUserId
  }
  return next
}

function isCurrentLookup(row: MemberRow, key: string, lookupId: number) {
  return row.key === key && row.lookupId === lookupId
}

export function useMemberRows(memberOrgIds: readonly string[]) {
  const { fetcherOptions } = useV3ApiContext()
  const [rows, setRows] = useState<MemberRow[]>([])
  const [entryError, setEntryError] = useState<EntryError | undefined>()

  const rowsRef = useRef<MemberRow[]>([])
  const nextLookupId = useRef(0)
  const latest = useRef({ fetcherOptions, memberOrgIds })
  latest.current = { fetcherOptions, memberOrgIds }

  const updateRows = useCallback((updater: RowsUpdater) => {
    rowsRef.current = updater(rowsRef.current)
    setRows(rowsRef.current)
  }, [])

  const runLookup = useCallback(
    (key: string, email: string, lookupId: number) => {
      fetchLookupUserIdentities({
        ...latest.current.fetcherOptions,
        queryParams: { email },
      })
        .then((lookup) => {
          updateRows((current) =>
            current.map((row) =>
              isCurrentLookup(row, key, lookupId)
                ? rowFromLookup(row, lookup, latest.current.memberOrgIds)
                : row,
            ),
          )
        })
        .catch((error: unknown) => {
          const isCurrent = rowsRef.current.some((row) =>
            isCurrentLookup(row, key, lookupId),
          )
          if (!isCurrent) {
            return
          }
          if (error instanceof ApiError && error.status === 400) {
            updateRows((current) => current.filter((row) => row.key !== key))
            setEntryError({ kind: 'invalid', email })
            return
          }
          updateRows((current) =>
            current.map((row) =>
              isCurrentLookup(row, key, lookupId)
                ? {
                    key: row.key,
                    email: row.email,
                    role: row.role,
                    lookupId: row.lookupId,
                    status: 'lookupFailed',
                  }
                : row,
            ),
          )
        })
    },
    [updateRows],
  )

  const addEmail = useCallback(
    (text: string): boolean => {
      setEntryError(undefined)
      const email = text.trim()
      if (email === '') {
        return false
      }
      const key = normalizeEmail(email)
      if (rowsRef.current.some((row) => row.key === key)) {
        setEntryError({ kind: 'duplicate' })
        return false
      }
      if (rowsRef.current.length >= MAX_ROWS) {
        setEntryError({ kind: 'limit' })
        return false
      }
      nextLookupId.current += 1
      const lookupId = nextLookupId.current
      updateRows((current) => [
        ...current,
        { key, email, role: 'viewer', lookupId, status: 'pending' },
      ])
      runLookup(key, email, lookupId)
      return true
    },
    [runLookup, updateRows],
  )

  const removeRow = useCallback(
    (key: string) => {
      updateRows((current) => current.filter((row) => row.key !== key))
    },
    [updateRows],
  )

  const retryLookup = useCallback(
    (key: string) => {
      const row = rowsRef.current.find((candidate) => candidate.key === key)
      if (!row) {
        return
      }
      nextLookupId.current += 1
      const lookupId = nextLookupId.current
      updateRows((current) =>
        current.map((candidate) =>
          candidate.key === key
            ? {
                key: candidate.key,
                email: candidate.email,
                role: candidate.role,
                lookupId,
                status: 'pending',
              }
            : candidate,
        ),
      )
      runLookup(key, row.email, lookupId)
    },
    [runLookup, updateRows],
  )

  const updateRow = useCallback(
    (key: string, patch: MemberRowPatch) => {
      updateRows((current) =>
        current.map((row) => (row.key === key ? applyPatch(row, patch) : row)),
      )
    },
    [updateRows],
  )

  return { rows, entryError, addEmail, removeRow, retryLookup, updateRow }
}
